import { API_BASE_URL } from "../../../config/api";
import { getToken } from "../../../shared/data/storage/authStorage";

/** One amount column. Trial balance rows carry net_debit/net_credit, the rest `total`. */
export interface ReportValue {
  total?: number;
  net_debit?: number;
  net_credit?: number;
}

export interface ReportNode {
  name: string;
  node_name: string | null;
  account_id: string | null;
  account_code: string | null;
  depth: number;
  is_group: boolean;
  is_formula: boolean;
  total_label: string | null;
  values: ReportValue[];
  children: ReportNode[];
}

export interface ReportColumn {
  label: string;
  from_date: string | null;
  to_date: string | null;
}

export interface ReportPageContext {
  report_type: string;
  report_basis: "Accrual" | "Cash" | null;
  filter_by: string;
  applied_filter: string | null;
  from_date: string | null;
  to_date: string | null;
  as_of_date: string | null;
  date_range_label: string | null;
  show_rows: string | null;
  is_as_of: boolean;
  has_basis: boolean;
}

export interface ZohoReportDTO {
  report: string;
  root: ReportNode | null;
  columns: ReportColumn[];
  amount_fields: string[];
  page_context: ReportPageContext;
}

export interface ReportQuery {
  filter_by: string;
  from_date: string;
  to_date: string;
  cash_based?: boolean;
}

export class ReportsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fieldErrors: Record<string, string> = {}
  ) {
    super(message);
    this.name = "ReportsApiError";
  }
}

const REPORTS_API_PATH = `${API_BASE_URL}/api/v1/reports/zoho`;

/**
 * GET /api/v1/reports/zoho/{report}. Same fetch + bearer pattern as the other
 * feature data sources. Live every call — there is no cache by design.
 */
export class ReportsApiDataSource {
  async fetchReport(report: string, query: ReportQuery, signal?: AbortSignal): Promise<ZohoReportDTO> {
    const params = new URLSearchParams({
      filter_by: query.filter_by,
      from_date: query.from_date,
      to_date: query.to_date,
    });
    if (query.cash_based) params.set("cash_based", "true");

    const token = getToken();
    const response = await fetch(`${REPORTS_API_PATH}/${report}?${params.toString()}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });

    let body: any = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    if (!response.ok || !body || body.success === false) {
      const message: string = body?.message || `Request failed (${response.status})`;
      const fieldErrors: Record<string, string> =
        body?.errors && typeof body.errors === "object" ? body.errors : {};
      throw new ReportsApiError(message, response.status, fieldErrors);
    }

    return body.data as ZohoReportDTO;
  }
}

export const reportsApiDataSource = new ReportsApiDataSource();
